import { useCallback, useState } from 'react';

import { defaultCommunityTranslate } from '../utils/defaultCommunityTranslate';

import type { CommunitySchedulePickerTranslate } from './_components/schedulePicker/SchedulePickerFrame';
import type { ComposerScheduling } from './CommunityPostComposer.type';
import type { CommunitySchedulePickerProps } from './CommunitySchedulePicker';

export interface UseComposerSchedulingOptions {
  canSchedule: boolean;
  queuedCount?: number;
  nextUsualTime?: string | null;
  /** The time of the scheduled post being edited, which the chip shows. */
  editedScheduledAt?: Date;
  /** Language tag for the date on the chip. Defaults to English. */
  locale?: string;
  translate?: CommunitySchedulePickerTranslate;
  /** Runs when the time is picked. */
  onChange?: () => void;
  /** The panel the dialog opens on. Defaults to the composer. */
  initialPanel?: ComposerSchedulingPanel;
}

type ComposerSchedulingPanel = 'composer' | 'picker' | 'queue';

// The publish time of a post, between the composer's clock and chip, the schedule picker and the team's queue, which
// take turns in the composer's dialog. The host loads the queue, sends the post and shows its own errors.
export const useComposerScheduling = ({
  canSchedule,
  queuedCount,
  nextUsualTime,
  editedScheduledAt,
  locale = 'en',
  translate = defaultCommunityTranslate,
  onChange,
  initialPanel,
}: UseComposerSchedulingOptions) => {
  const [scheduledAt, setScheduledAt] = useState(editedScheduledAt);
  const [panel, setPanel] = useState<ComposerSchedulingPanel>(initialPanel ?? 'composer');
  const [canSubmitDraft, setCanSubmitDraft] = useState(false);
  const openPicker = () => setPanel('picker');
  const closePicker = () => setPanel('composer');
  const openQueue = () => setPanel('queue');

  const changeScheduledAt = (date: Date) => {
    setScheduledAt(date);
    onChange?.();
  };

  const label = scheduledAt
    ? translate('community.composer.schedule.chip', { date: formatScheduledAt(scheduledAt, locale) })
    : null;
  const scheduling: ComposerScheduling | undefined = canSchedule
    ? {
        onOpen: openPicker,
        onCanSubmitChange: setCanSubmitDraft,
        queuedCount,
        scheduled: label
          ? { label, changeLabel: translate('community.composer.schedule.change_time', { label }) }
          : undefined,
      }
    : undefined;

  // The backend refuses a time that is not in the future, so the host checks it before sending.
  const hasTimePassed = useCallback(() => Boolean(scheduledAt && scheduledAt <= new Date()), [scheduledAt]);
  const pickerAction: CommunitySchedulePickerProps['action'] = editedScheduledAt ? 'confirm' : 'schedule';
  const nextUsualDate = nextUsualTime ? new Date(nextUsualTime) : undefined;

  return {
    scheduledAt,
    /** Whether the edited scheduled post now has a time other than the one it was saved with. */
    isTimeChanged: Boolean(editedScheduledAt && scheduledAt?.getTime() !== editedScheduledAt.getTime()),
    scheduling,
    panel,
    /**
     * The picker's props. A new post is sent from the picker with onSchedule, at the time picked there, while the
     * composer says it can be. An edited scheduled post only takes the new time, which its Save sends.
     */
    pickerFor: (onSchedule: (scheduledAt: Date) => void) => ({
      value: scheduledAt,
      nextUsualTime: nextUsualDate,
      queuedCount,
      action: pickerAction,
      onConfirm: editedScheduledAt
        ? (date: Date) => {
            changeScheduledAt(date);
            closePicker();
          }
        : onSchedule,
      isSubmitDisabled: !editedScheduledAt && !canSubmitDraft,
      onCancel: closePicker,
      onViewQueue: openQueue,
    }),
    queue: {
      nextUsualTime: nextUsualDate,
      onBack: openPicker,
      onSchedule: openPicker,
    },
    hasTimePassed,
  };
};

// In the browser's time zone, like the picker: "Sat, Oct 3, 9:00 AM EDT".
const formatScheduledAt = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date);
